import { MessageDescriptor } from 'react-intl'
import { Edge, Node } from 'reactflow'

import { Audience } from '@/utilities/AudienceTarget/interfaces'

type Only<T, U> = {
  [P in keyof T]: T[P]
} & {
  [P in keyof U]?: never
}

export type Either<T, U> = Only<T, U> | Only<U, T>
export interface Workflow {
  trigger: Triggers
  steps: Step[]
}

export interface Feature {
  id?: string
  name: string
  description?: string
  isDefault?: boolean
  isQueryable?: boolean
  responseType?: 'rag' | 'verbatim'
  type?: string
  trigger?: string
  config?: Config[]
  workflow?: Workflow
  relatedFeatures?: RelatedFeature[]
  isDebugLoggingEnabled?: boolean
  templateFeatureId?: string
  status?: FeatureStatus
  createdBy?: string
  createdDate?: string
  modifiedBy?: string
  modifiedDate?: string
}

export type Config = {
  id?: string
  displayName: string
  name: string
  description?: string
  allowedValues?: string
  hasAllowedValues?: boolean
  isAppParameter?: boolean
  formInputType?: string
  required: boolean
  defaultValue?: string
  type: string
}

export type FlowElement = Node | Edge

export interface RelatedFeature {
  featureId: string
  name?: string
  stepId?: string
}

export interface App {
  metadata?: AppMetaData
  id: string
  name: string
  description: string
  audience?: Audience[]
  iconUrl?: string
  status?: string
  space?: string
  createdBy: string
  createdDate: string
  modifiedBy: string
  modifiedDate: string
  sharedWith?: string[]
  publishedDate?: number
  dataSources?: Array<AppInstanceDataSource>
  features?: Feature[]
  version?: string
  detailImage?: any
  template?: AppTemplateMetaData
}
export interface AppVersion {
  version: string
  date?: string
  changes: string[] | string
}
export interface Template {
  id: string
  name: string
  iconUrl: string
  description: string
  features: Feature[]
  dataSources?: DataSource[]
  metadata?: AppMetaData
  createdDate: string
  modifiedDate?: string
}
export interface AppMetaData {
  referenceLink?: string
  referenceLinks?: [referenceLink]
  heroUrl?: string
  content?: string
  endpointcontent?: string
  endpoints?: Array<string>
  credentialPrerequisites?: Array<string>
  credentialScopes?: Array<string>
  credentialGeneration?: Array<string>
  installationPrerequisites?: Array<string>
  installationRequirements?: Array<string>
  embeddedVideo?: EmbeddedVideo
  changelog?: AppVersion[]
  categories?: Array<string>
  featureFilters?: Array<string>
}
export interface referenceLink {
  link: string
  label: string
}

export interface AppTemplateMetaData {
  version: string
  templateId: string
  templateName: string
}

interface EmbeddedVideo {
  videoTitle?: string
  videoUrl?: string
  videoId?: string
}

export interface InitialApp {
  name: string
  dataSources: DataSource[]
  features: Feature[]
}

//todo expand with auth config
export interface DataSource {
  id: string
  name: string
  vendorName: string
  description: string
  iconUrl: string
  baseUrl: string
  type: string
  key: string
  isUserAuth: boolean
}

export interface DataSourceMapping {
  isPaginated?: boolean
  path?: string
  method: string
  endpoint?: string
  endpointName: string
  responseObjectName?: string
  description: string
  parameters: any
  response: any
}

interface ArrayItem {
  type: string
  properties?: VariableProperties
}

interface properties {
  name: string
  propertyName: string
  description: string
  required: boolean
  format?: string
  example?: string
  'x-samplevalue'?: string
  'x-displayName'?: string
  'x-alias'?: string
  properties: any
  enum?: Array<string>
  type: string
  items?: ArrayItem
}

export interface VariableProperties {
  [key: string]: properties
}

export type WorkgridCatalogDatasource = {
  id: string
  iconUrl?: string
  vendorApiUrl?: string
  supportUrl?: string
  name: string
  version: string
  schema: Record<string, any>
  actions: Record<string, any>
  events: Record<string, any>
  authTypes: string[]
  type: string
}

export interface WorkgridDatasource {
  datasourceId: string
  id: string
  name: string
  configuration: Record<string, any>
  status: string
  userId: string
  createdBy: string
  createdDate: string
  modifiedBy: string
  modifiedDate: string
  owners?: string[]
  applicationIds?: string[]
}

export interface WorkgridUserDatasourceInstance {
  id: string
  datasourceId: string
  createdBy: string
  createdDate: string
  modifiedBy: string
  modifiedDate: string
  status?: string
}

export enum DatasourceWizardMode {
  CREATE,
  EDIT,
  IMPORT,
  PREVIEW
}

export interface Operation {
  name: string
  label?: MessageDescriptor | string
  description?: MessageDescriptor
  subLabel?: MessageDescriptor
  defaultStepData?: any
  type: string
  draggable?: boolean
  verticalOffset?: number
  horizontalOffset?: number
  tooltip?: string
  icon?: any
  iconUrl?: string
}

export enum StepType {
  Log = 'output',
  DataSource = 'data-source-action',
  WorkgridDataSource = 'workgrid-datasource-action',
  Boolean = 'boolean',
  Loop = 'loop',
  CustomCode = 'script',
  Notification = 'send-smart-notification',
  NotificationDateFormatter = 'notification-date-formatter',
  NotificationDelete = 'delete-notification',
  APIRequest = 'data-source-api-request',
  DataStorage = 'data-storage',
  DataTransform = 'data-transform-utility',
  Response = 'response',
  NotificationView = 'app-ui-notification',
  RunFeature = 'callable-feature',
  DeployFeature = 'deploy-feature',
  UserLookup = 'user-lookup',
  DocumentTextSplitter = 'document-text-splitter',
  TextToEmbeddings = 'text-to-embeddings',
  KnnSearch = 'search-embeddings',
  RagPrompt = 'rag-prompt',
  FileUpload = 'file-upload',
  FileDownload = 'file-download',
  AdaptiveCard = 'adaptive-card',
  SetConfigVariable = 'set-config-variable',
  DocumentSearchStorage = 'document-search-storage',
  InvokeLambda = 'invoke-lambda',
  GenerateMicroappToken = 'generate-microapp-token'
}

export enum OperationType {
  dataSource = 'dataSource',
  operation = 'operation',
  notificationOperation = 'notificationOperation',
  advancedOperation = 'advancedOperation',
  aiOperation = 'aiOperation',
  dataTransformOperation = 'dataTransform'
}

export enum CustomNodes {
  placeHolderNode = 'placeHolderNode',
  stepNode = 'dataNode',
  triggerNode = 'triggerNode'
}

export enum PlaceholderNodeTypes {
  conditionalTrueStep = 'conditionalTrueStep',
  conditionalFalseStep = 'conditionalFalseStep',
  loopStep = 'loopStep'
}

interface NamedObject {
  name: string
}

export interface SourcePropertyObject {
  properties?: any // An object of [key: string]: SourcePropertyObject
  [key: string]: any
}

export interface LabeledDataObject {
  data: {
    label?: string
    iconUrl?: string
    icon?: string
  }
}

export interface Step extends NamedObject {
  id: string
  label: string
  type: StepType
  stepData: any
  output?: any
  parent?: string
  stepCount?: number
  condtionalTrack?: string
  onSuccess?: string[]
  errorConfig?: { hasErrors?: boolean; showValid?: boolean; showValidExecution?: boolean }
}

export interface Variables {
  [key: string]: Variable[]
}

export interface Variable {
  name: string
  stepName: string | undefined
  stepLabel: string
  stepType?: StepType | TriggerTypes
  displayName: string
  example?: string
  type: string
  enum?: Array<string>
  isNested?: boolean
  icon?: string | JSX.Element
  properties?: Variable[]
  items?: ArrayItem
  itemsType?: string
  isHidden?: boolean
}

export interface WorkgridDatasourceStepData {
  inputs: any
  action: string
  dataSourceKey: string
}

export interface LogStepData {
  message: string
}

export interface SetConfigVariableStepData {
  configVariable: string
  configValue: string
  shouldAppend?: boolean
}

export interface DocumentSearchStorageStepData {
  mode: 'insert' | 'delete'
  document?: string
  queryableFeatureId?: string
  deleteDocumentId?: string
  insertDocumentId?: string
}

export interface UserLookupData {
  searchMode: 'name' | 'email' | 'user ID' | 'filter'
  familyName?: string
  givenName?: string
  matchFirstAndLastName?: boolean
  userId?: string
  email?: string
  filter?: string
  getManager?: boolean
  getDirectReports?: boolean
  getPeers?: boolean
}

export interface FileUploadData {
  attachment: any
  uploadMode?: string
  url: string
  expire?: boolean
}

export interface CustomCodeStepData {
  parameters: Param[]
  body: string
}

export interface AdaptiveCardStepData {
  parameters?: Param[]
  payload: string
  data?: string
}

interface Param {
  key: string
  value: string
}
interface ResponseParam {
  id: string
  value: string
}

interface Filter {
  leftComparator: string
  operator: string
  rightComparator: string
}

interface Condition {
  filters: Filter[]
  expression: string
  id: string
}

export interface BooleanStepData {
  conditions: Condition[]
  onTrue: string[]
  onFalse: string[]
}

interface NotificationData {
  summary: {
    title: string
    body: string
    image?: object
  }
  location?: string
  detail: object[]
  actions: object[]
  actionData: ActionData[]
  category: string
  from: string | Object
  audience: object[]
  notificationSource: string
  priority?: string
  hiddenAfter: string
  isHiddenBefore?: string // Client-only field
  hiddenBefore?: string
  isHiddenBeforeRelativeToUserTimezone?: string
  correlationId?: string
  shouldHaveActionFeature?: boolean
}

export interface LoopStepData {
  inputVariable: string
  loopSteps: Step[]
}

export interface DataSourceStepData {
  dataSourceKey: string
  endpoint: string
  method?: string
  path?: string
  input: DataSourceStepDataInput
}

interface DataSourceStepDataInput {
  path: any
  query: {
    fields: any
    filters: any
    params: any
  }
  body: any
}

export interface NotificationStepData {
  notifications: NotificationData[]
  type: 'notification.create'
}

interface NotificationViewData {
  version: string
  correlationId?: string
  audience: object[]
  notificationSource: string
  location: string
  priority?: string
  hiddenAfter: string | object
  hiddenBefore?: string | object
  actionLabel?: string
  isHiddenBefore?: string // Client-only field
  entrypoint: string
  category: string
  from: string | Object
  title: string
  body: string
  isHiddenBeforeRelativeToUserTimezone?: string
}

export interface NotificationViewStepData {
  data: NotificationViewData
  type: 'notification.create'
}

export interface APIRequestStepData {
  dataSourceKey: string
  path: string
  method: string
  input: {
    path?: any
    query: APIRequestInputQuery[]
    body?: any
  }
}

export interface APIRequestInputQuery {
  id: string
  key: string
  value: string
}

export interface DateFormatterStepData {
  dateFormatFunctionType?: string
  dateInputVariable?: string
  dateInputVariableDisplayName?: string
  format: 'long' | 'short'
  includeTime: boolean
}

export interface DeleteNotificationStepData {
  correlationId: string
  audience?: any[]
}

export interface DataStorageStepData {
  operation: string
  action: string
  objectName: string
  keyIdentifier: string
  data: string
  expiresAfter: string
}

export interface DataTransformStepData {
  actionName?: string
  actions: DataTransformAction[]
  inputVariable: string
}

export interface ResponseStepData {
  inputVariable?: string
  parameters: ResponseParam[]
}
export interface InvokeLambdaStepData {
  inputData: string
  lambdaArn: string
}
export interface GenerateMicroappTokenStepData {
  audience: string
  appId?: string
}
export interface FileDownloadStepData {
  assetId: string
}

export interface RunFeatureStepData {
  isAsync: boolean
  featureId: string
  payloadVariable: string
}

export interface DeployFeatureStepData {
  mode: 'deployApp' | 'deployFeature'
}

export interface DocumentTextSplitterStepData {
  documentText: string
  chunkSize?: number
  chunkOverlap?: number
}

export interface TextToEmbeddingsStepData {
  text: string
}

export interface KnnSearchStepData {
  question: string
  documentChunks: string
  numberOfNeighboursToReturn?: number
}

export interface RagPromptStepData {
  knnChunks: string
  question: string
}

export interface DataTransformAction {
  id?: string
  action: string
  inputType?: string
  parameters?: object
}

interface ActionConfigExampleInput {
  parameters?: any
  data: any
}

export interface ActionConfigExample {
  input: ActionConfigExampleInput
  output: any
}

export interface ActionConfigProps {
  name: string
  displayName: string
  inputType: string
  outputType: string
  description: string
  parametersSchema?: any | undefined
  example?: ActionConfigExample | undefined
}

export type StepData =
  | LogStepData
  | NotificationStepData
  | LoopStepData
  | DataSourceStepData
  | BooleanStepData
  | CustomCodeStepData
  | DateFormatterStepData
  | APIRequestStepData
  | DeleteNotificationStepData
  | DataStorageStepData
  | DataTransformStepData
  | ResponseStepData
  | NotificationViewStepData
  | RunFeatureStepData
  | UserLookupData
  | FileUploadData
  | FileDownloadStepData
  | DocumentTextSplitterStepData
  | TextToEmbeddingsStepData
  | KnnSearchStepData
  | RagPromptStepData
  | AdaptiveCardStepData
  | SetConfigVariableStepData
  | DocumentSearchStorageStepData
  | WorkgridDatasourceStepData
  | DeployFeatureStepData
  | InvokeLambdaStepData
  | GenerateMicroappTokenStepData

export enum TriggerTypes {
  manual = 'manual',
  scheduled = 'scheduled',
  dataSourceEvent = 'dataSourceEvent',
  none = 'none',
  notificationAction = 'notificationAction',
  http = 'http',
  microappView = 'microappView',
  featureTrigger = 'callableFeature',
  knowledge = 'knowledge'
}

interface BaseTrigger {
  type: string
  name?: string
  id?: string
  errorConfig?: { hasErrors?: boolean; showValid?: boolean; showValidExecution?: boolean }
  parentFeature?: string
  stepId?: string
}

export interface NoTrigger extends BaseTrigger {
  type: TriggerTypes.none
  triggerData: any
}

export interface DataSourceEventTrigger extends BaseTrigger {
  type: TriggerTypes.dataSourceEvent
  triggerData: DataSourceEventTriggerData
}

export interface DataSourceEventTriggerData {
  triggerSourceImageUrl: string
  triggerEvent: string
  pollingInterval?: number
  triggerSourceKey: string
  watchedFields?: string[]
}

interface HttpTriggerData {
  clientId: string
  payload: object
}

export interface ManualTrigger extends BaseTrigger {
  type: TriggerTypes.manual
  triggerData: any
}

export interface HttpTrigger extends BaseTrigger {
  type: TriggerTypes.http
  triggerData: HttpTriggerData
}

export interface ScheduledTrigger extends BaseTrigger {
  type: TriggerTypes.scheduled
  triggerData: ScheduledTriggerData
}

export interface NotificationActionTrigger extends BaseTrigger {
  type: TriggerTypes.notificationAction
  triggerData: any
}

export interface MicroappViewTrigger extends BaseTrigger {
  type: TriggerTypes.microappView
  triggerData: any
}

export interface FeatureTrigger extends BaseTrigger {
  type: TriggerTypes.featureTrigger
  triggerData: any
}
export interface KnowledgeTrigger extends BaseTrigger {
  type: TriggerTypes.knowledge
  triggerData: any
}

export interface TriggerInput {
  type: TriggerTypes.dataSourceEvent
  inputVariablePath: string
}

export type TriggerConfig = Partial<
  | ManualTrigger
  | ScheduledTrigger
  | DataSourceEventTrigger
  | FeatureTrigger
  | NotificationActionTrigger
  | MicroappViewTrigger
  | KnowledgeTrigger
>

export type Triggers =
  | DataSourceEventTrigger
  | NoTrigger
  | ManualTrigger
  | ScheduledTrigger
  | FeatureTrigger
  | NotificationActionTrigger
  | MicroappViewTrigger
  | KnowledgeTrigger

export interface PlaceholderNodeData {
  type: PlaceholderNodeTypes
  previousStep: string
  parentStep: string
  nextStep?: Node | undefined
  canDrop?: boolean
  isDraggingNearNode?: boolean
}

export interface AppInstanceDataSource {
  id: string
  applicationId: string
  datasourceId?: string
  templateDatasourceId?: string
  key: string
  isUserAuth: boolean
}

export interface AppInstanceDataSourceItems {
  items: [AppInstanceDataSource]
}

export interface NotificationSource {
  connectorInstanceId: string
  iconUrl: string
  id: string
  isDefault: boolean
  source: string
  spaceId: string
  title: string
}

export enum FeatureStatus {
  DEPLOYING = 'deploying',
  DEACTIVATING = 'deactivating',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
  PREVIEW = 'preview'
}

export enum ExecutionStepStatus {
  Success = 'success',
  Pending = 'pending',
  Failed = 'failed'
}

export enum NotificationStatus {
  active = 'active',
  expired = 'expired',
  recalled = 'recalled'
}

export interface FeatureExecutionStep {
  status: ExecutionStepStatus
  createdDate: string
  updatedDate?: string
  stepName: string
  stepType: string
  input: Input
  output: Output
  notificationStatus?: NotificationStatus
}

export interface NetworkObject {
  name: string
  triggerData: any
  stepData: any
}

interface Input {
  query?: {}
  body?: any
  event?: any
}

interface Output {
  metaData?: any
  body: any
  code: number
}

export type ScheduledTriggerData =
  | OneTimeFrequencyData
  | MinutesFrequencyData
  | HourlyFrequencyData
  | DailyFrequencyData
  | WeeklyFrequencyData
  | MontlyFrequencyData
  | YearlyFrequencyData

export enum RepeatOptions {
  OneTime = 'oneTime',
  Minutes = 'minutes',
  Hourly = 'hourly',
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
  MonthlyConcise = 'monthlyConcise',
  MonthlyVerbose = 'monthlyVerbose',
  Yearly = 'yearly',
  YearlyConcise = 'yearlyConcise',
  YearlyVerbose = 'yearlyVerbose'
}

interface BaseFrequencyData {
  repeats: RepeatOptions
  isUserSchedule: boolean
  timeZone: string
  audience: Array<any>
}
export interface OneTimeFrequencyData extends BaseFrequencyData {
  date: string
}
export interface MinutesFrequencyData extends BaseFrequencyData {
  minutes: number
}
export interface HourlyFrequencyData extends BaseFrequencyData {
  hours: number
}
export interface DailyFrequencyData extends BaseFrequencyData {
  time: string | Date
}
export interface WeeklyFrequencyData extends BaseFrequencyData {
  days: string[]
  time: string | Date
}
type MontlyFrequencyData = MontlyConciseFrequencyData | MontlyVerboseFrequencyData
type YearlyFrequencyData = YearlyConciseFrequencyData | YearlyVerboseFrequencyData

export interface MontlyConciseFrequencyData extends BaseFrequencyData {
  months: number
  time: string | Date
  day: number
}
export interface MontlyVerboseFrequencyData extends BaseFrequencyData {
  months: number
  time: string | Date
  isVerbose: string
  weekOfMonth: number
  dayOfWeek: number
}
export interface YearlyConciseFrequencyData extends BaseFrequencyData {
  dayMonth: string | Date
  time: string | Date
}
export interface YearlyVerboseFrequencyData extends BaseFrequencyData {
  time: string | Date
  weekOfMonth: number
  monthOfYear: number
  dayOfWeek: number
}

export enum FieldTypes {
  INTEGER = 'integer',
  NUMBER = 'number',
  STRING = 'string',
  BOOLEAN = 'boolean',
  OBJECT = 'object',
  ARRAY = 'array',
  ANY = 'any'
}
export enum FieldFormats {
  TEXT = 'text',
  NUMBER = 'number',
  INT = 'int',
  FLOAT = 'float',
  DOUBLE = 'double',
  DATE_TIME = 'date-time',
  ARRAY = 'array'
}

export interface ActionData {
  id: string
  attributeName: string
  attributeValue: string
  inputType?: string
  isRequired?: boolean
}

export interface BasicValidationError {
  key: string
  id: string
}

export enum DataStorageOperations {
  MODIFY = 'modify',
  READ = 'read'
}

export enum DataStorageActions {
  APPEND = 'append',
  REPLACE = 'replace',
  GET_ALL = 'get-all-object-records',
  GET_BY_KEY = 'get-record-by-key',
  DELETE_ALL = 'delete-all-object-records',
  DELETE_BY_KEY = 'delete-record-by-key'
}

export enum DataStorageExpiration {
  ONE_DAY = '86400',
  ONE_WEEK = '604800',
  ONE_MONTH = '2630000',
  THREE_MONTHS = '7890000',
  SIX_MONTHS = '15780000',
  ONE_YEAR = '31536000',
  LIFETIME = ''
}

export const ActionTypes = {
  ActionOpenUrl: 'Action.OpenUrl',
  ActionSubmit: 'Action.Submit'
}

export interface ActionBlockDataType {
  blockId: string
  actionType: string
  displayInSummary: boolean
  type: 'Action.Submit' | 'Action.OpenUrl'
  title: string
  url: string
}

export enum ExecutionStepsMode {
  TEST_RUN,
  HISTORY,
  DEPLOYED_RUN
}
export enum FileUploadMode {
  DIRECTORY = 'upload from directory',
  URL = 'upload from url'
}

export interface HistoryRecordType {
  id: string
  status: 'string'
  debugLoggingEnabled: boolean
  createdDate: 'string'
  updatedDate: 'string'
  executionDate: 'string'
}
